import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { 
    ImagePlus, Trash2, Edit, ChevronLeft, 
    Save, Image as ImageIcon, Star, StarOff, MessageSquare, Plus
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

// ==========================================
// STYLED COMPONENTS (WSPÓŁDZIELONE)
// ==========================================

const ManagerContainer = styled.div`
  width: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;

  h2 {
    margin: 0;
    font-size: 1.8rem;
    color: #111;
    font-weight: 800;
  }
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #111;
  color: #fff;
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s;

  &:hover { background: #333; transform: translateY(-2px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;

const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  color: #111;
  padding: 10px 18px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover { background: #f4f6f8; }
`;

// --- KARTY FORMULARZY ---

const FormCard = styled.form`
  background: #fff;
  border-radius: 12px;
  padding: 30px;
  border: 1px solid #eee;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03);
  margin-bottom: 30px;
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 40px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const FormSectionTitle = styled.h4`
  margin: 0 0 20px 0;
  font-size: 1.1rem;
  color: #111;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  svg { color: #888; }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;

  label { font-size: 0.9rem; font-weight: 700; color: #555; }
  input, textarea {
    padding: 12px 14px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 1rem;
    font-family: inherit;
    transition: border-color 0.3s;
    &:focus { outline: none; border-color: #111; }
  }
`;

// --- WIDOK LISTY (SIATKA KAFELKÓW ZDJĘĆ) ---

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 25px;
  align-items: start;
`;

const PhotoCard = styled.div`
  background: #fff;
  border-radius: 12px;
  border: 1px solid #eee;
  box-shadow: 0 4px 15px rgba(0,0,0,0.03);
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;

  .image-wrapper {
    width: 100%;
    aspect-ratio: 4/3;
    overflow: hidden;
    position: relative;
    background: #f4f6f8;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s ease;
    }
  }

  &:hover .image-wrapper img {
    transform: scale(1.05);
  }

  /* Badge na zdjęciu, informujący, że jest w głównym sliderze */
  .slider-badge {
    position: absolute;
    top: 10px;
    right: 10px;
    background: #FFD700;
    color: #000;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 5px;
    z-index: 2;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  }

  .content {
    padding: 15px;
    
    p.desc {
      margin: 0;
      color: #111;
      font-weight: 600;
      font-size: 0.95rem;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    p.date {
      margin: 5px 0 0 0;
      font-size: 0.8rem;
      color: #999;
    }
  }

  .stats {
    padding: 0 15px 15px 15px;
    display: flex;
    gap: 15px;
    color: #888;
    font-size: 0.85rem;

    div { display: flex; align-items: center; gap: 5px; }
    svg { width: 14px; height: 14px; }
  }

  .actions {
    display: flex;
    border-top: 1px solid #f0f0f0;

    button {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 5px;
      padding: 12px;
      border: none;
      background: #fafafa;
      font-weight: 600;
      font-size: 0.85rem;
      cursor: pointer;
      transition: 0.2s;
    }

    .btn-edit { color: #234ffc; border-right: 1px solid #f0f0f0; }
    .btn-edit:hover { background: #eef2ff; }

    .btn-delete { color: #dc3545; }
    .btn-delete:hover { background: #fff0f0; }
  }
`;

const PreviewBox = styled.div`
  width: 100%;
  aspect-ratio: 4/3;
  border-radius: 12px;
  background: #f4f6f8;
  border: 2px dashed #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  p {
    color: #999;
    font-weight: 600;
  }
`;

const ToggleSwitch = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 15px;
  border-radius: 8px;
  background: ${props => props.active ? '#fff9e6' : '#fafafa'};
  border: 1px solid ${props => props.active ? '#ffeeba' : '#eee'};
  transition: all 0.3s;

  input {
    display: none;
  }

  .slider {
    width: 46px;
    height: 24px;
    background-color: ${props => props.active ? '#FFD700' : '#ccc'};
    border-radius: 34px;
    position: relative;
    transition: 0.4s;

    &::before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background-color: white;
      transition: 0.4s;
      border-radius: 50%;
      transform: ${props => props.active ? 'translateX(22px)' : 'translateX(0)'};
    }
  }

  .text {
    font-weight: 700;
    color: ${props => props.active ? '#856404' : '#666'};
    display: flex;
    flex-direction: column;
    
    span {
      font-size: 0.8rem;
      font-weight: 400;
      color: #999;
    }
  }
`;

// ==========================================
// GŁÓWNY KOMPONENT
// ==========================================

export default function NewPhotoManager() {
    const [photos, setPhotos] = useState([]);
    
    // URL Params
    const [searchParams, setSearchParams] = useSearchParams();
    const viewState = searchParams.get("action") || "list";
    const editingId = searchParams.get("id") || null;

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({ description: "", inSlider: false, linkUrl: "" });
    const [imageFile, setImageFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(""); // Do wyświetlania na żywo
    const dataLoadedRef = useRef(null);

    const adresAPI = "https://api.pth-hokej.pl";

    const fetchPhotos = async () => {
        try {
            const res = await fetch(`${adresAPI}/api/gallery`);
            const data = await res.json();
            setPhotos(data);
        } catch (err) {
            console.error("Błąd pobierania galerii:", err);
        }
    };

    useEffect(() => { 
        fetchPhotos(); 
    }, []);

    // AUTO-WYPEŁNIANIE PO ODŚWIEŻENIU (F5)
    useEffect(() => {
        if (viewState === "edit" && editingId && photos.length > 0) {
            if (dataLoadedRef.current !== editingId) {
                const item = photos.find(p => p._id === editingId);
                if (item) {
                    setFormData({ 
                        description: item.description || "", 
                        inSlider: item.inSlider || false,
                        linkUrl: item.link || ""
                    });
                    setPreviewUrl(item.link);
                    setImageFile(null);
                    dataLoadedRef.current = editingId;
                }
            }
        } else if (viewState === "list") {
            dataLoadedRef.current = null;
        }
    }, [viewState, editingId, photos]);

    // Odsługa podglądu pliku z komputera
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setImageFile(file);
        if (file) {
            const objectUrl = URL.createObjectURL(file);
            setPreviewUrl(objectUrl);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (viewState === "add") {
                // DODAWANIE NOWEGO ZDJĘCIA
                const submitData = new FormData();
                submitData.append("description", formData.description);
                submitData.append("inSlider", formData.inSlider);
                
                if (imageFile) {
                    submitData.append("photo", imageFile);
                } else if (formData.linkUrl) {
                    submitData.append("linkUrl", formData.linkUrl);
                } else {
                    alert("Wybierz plik lub podaj link!");
                    setLoading(false);
                    return;
                }

                await fetch(`${adresAPI}/api/gallery`, {
                    method: "POST",
                    body: submitData,
                });
            } else if (viewState === "edit") {
                // EDYCJA (Aktualizujemy tylko opisy i flagę slidera, bez podmieniania fizycznego pliku dla uproszczenia UX)
                await fetch(`${adresAPI}/api/gallery/${editingId}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        description: formData.description,
                        inSlider: formData.inSlider
                    }),
                });
            }

            await fetchPhotos();
            setSearchParams({}); 
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Na pewno chcesz bezpowrotnie usunąć to zdjęcie z galerii?")) return;
        try {
            await fetch(`${adresAPI}/api/gallery/${id}`, { method: "DELETE" });
            fetchPhotos();
        } catch (err) {
            console.error(err);
        }
    };

    const handleAddClick = () => {
        setFormData({ description: "", inSlider: false, linkUrl: "" });
        setImageFile(null);
        setPreviewUrl("");
        setSearchParams({ action: "add" });
    };

    const handleEditClick = (item) => {
        setFormData({ description: item.description || "", inSlider: item.inSlider || false, linkUrl: item.link || "" });
        setImageFile(null);
        setPreviewUrl(item.link);
        dataLoadedRef.current = item._id;
        setSearchParams({ action: "edit", id: item._id });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <ManagerContainer>
            
            {/* WIDOK LISTY ZDJĘĆ */}
            {viewState === "list" && (
                <>
                    <HeaderRow>
                        <h2>Galeria i Slider</h2>
                        <PrimaryButton onClick={handleAddClick}>
                            <Plus size={20} /> Dodaj Zdjęcie
                        </PrimaryButton>
                    </HeaderRow>

                    {photos.length === 0 ? (
                        <p style={{ color: "#666" }}>Galeria jest pusta.</p>
                    ) : (
                        <GalleryGrid>
                            {photos.map((item) => (
                                <PhotoCard key={item._id}>
                                    {item.inSlider && (
                                        <div className="slider-badge">
                                            <Star size={14} fill="#000" /> W SLIDERZE
                                        </div>
                                    )}
                                    <div className="image-wrapper">
                                        <img src={item.link} alt="Galeria" />
                                    </div>
                                    
                                    <div className="content">
                                        <p className="desc">{item.description || "Brak opisu"}</p>
                                        <p className="date">Dodano: {new Date(item.uploadedAt).toLocaleDateString('pl-PL')}</p>
                                    </div>

                                    <div className="stats">
                                        <div><MessageSquare /> {item.coms?.length || 0}</div>
                                    </div>

                                    <div className="actions">
                                        <button className="btn-edit" onClick={() => handleEditClick(item)}>
                                            <Edit size={16} /> Edytuj
                                        </button>
                                        <button className="btn-delete" onClick={() => handleDelete(item._id)}>
                                            <Trash2 size={16} /> Usuń
                                        </button>
                                    </div>
                                </PhotoCard>
                            ))}
                        </GalleryGrid>
                    )}
                </>
            )}

            {/* WIDOK FORMULARZA (DODAWANIE / EDYCJA) */}
            {(viewState === "add" || viewState === "edit") && (
                <>
                    <HeaderRow>
                        <SecondaryButton onClick={() => setSearchParams({})}>
                            <ChevronLeft size={20} /> Wróć do galerii
                        </SecondaryButton>
                        <h2>{viewState === "edit" ? "Edytuj Zdjęcie" : "Dodaj Zdjęcie"}</h2>
                    </HeaderRow>

                    <FormCard onSubmit={handleSubmit}>
                        
                        {/* Lewa kolumna: Formularz */}
                        <div>
                            <FormSectionTitle><ImageIcon size={20}/> Ustawienia obrazu</FormSectionTitle>
                            
                            {/* Opcja wgrywania widoczna tylko przy nowym zdjęciu. Przy edycji edytujemy tylko teksty! */}
                            {viewState === "add" && (
                                <FormGroup style={{ background: '#fafafa', padding: '15px', borderRadius: '8px', border: '1px solid #eee' }}>
                                    <label style={{marginBottom: '5px'}}>Źródło (Wybierz opcję 1 lub 2)</label>
                                    <div style={{ display: 'grid', gap: '15px' }}>
                                        <div>
                                            <span style={{ fontSize: '0.8rem', color: '#888', display: 'block' }}>Z komputera:</span>
                                            <input type="file" accept="image/*" onChange={handleFileChange} style={{ background: '#fff', width: '100%', padding: '8px', boxSizing: 'border-box' }} disabled={!!formData.linkUrl} />
                                        </div>
                                        <div>
                                            <span style={{ fontSize: '0.8rem', color: '#888', display: 'block' }}>Z linku URL:</span>
                                            <input name="linkUrl" value={formData.linkUrl} onChange={(e) => { setFormData({...formData, linkUrl: e.target.value}); setPreviewUrl(e.target.value); }} placeholder="https://..." disabled={!!imageFile} />
                                        </div>
                                    </div>
                                </FormGroup>
                            )}

                            <FormGroup>
                                <label>Krótki opis / Tytuł</label>
                                <input 
                                    name="description" 
                                    placeholder="np. Radość Wilków po strzelonym golu!" 
                                    value={formData.description} 
                                    onChange={(e) => setFormData({...formData, description: e.target.value})} 
                                />
                            </FormGroup>

                            <FormSectionTitle style={{ marginTop: '30px' }}><Star size={20}/> Ekran Główny</FormSectionTitle>
                            
                            <ToggleSwitch active={formData.inSlider}>
                                <input 
                                    type="checkbox" 
                                    checked={formData.inSlider} 
                                    onChange={(e) => setFormData({...formData, inSlider: e.target.checked})} 
                                />
                                <div className="slider"></div>
                                <div className="text">
                                    Pokaż w Sliderze
                                    <span>To zdjęcie pojawi się wielkie na Stronie Głównej</span>
                                </div>
                            </ToggleSwitch>

                            <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                                <PrimaryButton type="submit" disabled={loading} style={{ width: '100%', justifyContent: 'center' }}>
                                    <Save size={20} /> {viewState === "edit" ? "Zapisz Zmiany" : "Dodaj do Galerii"}
                                </PrimaryButton>
                            </div>
                        </div>

                        {/* Prawa kolumna: Podgląd */}
                        <div>
                            <FormSectionTitle>Podgląd</FormSectionTitle>
                            <PreviewBox>
                                {previewUrl ? (
                                    <img src={previewUrl} alt="Podgląd" />
                                ) : (
                                    <p>Wybierz plik, aby zobaczyć podgląd</p>
                                )}
                            </PreviewBox>
                        </div>

                    </FormCard>
                </>
            )}
        </ManagerContainer>
    );
}